import * as React from 'react';

import { Row, Col } from 'antd/lib/grid';
import Collapse from 'antd/lib/collapse/Collapse';
import Typography from 'antd/lib/typography';

import { dictionary } from '@app/components/Pages/Admin/LabelPaper/LabelPaperEditor/LabelPaperEditorComponents/InnerFormSections/Names';
import {
	LabelData,
} from '@app/components/Pages/Admin/LabelPaper/LabelPaperEditor/LabelPaperEditorComponents/PaperPreview';

interface ChildrenProps {
	children: React.ReactNode;
	style?: React.CSSProperties;
}

export const PageRow: React.FC<ChildrenProps> = (props: ChildrenProps) => (
	<Row gutter={[30, 16]} style={props.style}>
		{props.children}
	</Row>
);

export const PageCol: React.FC<ChildrenProps> = (props: ChildrenProps) => (
	<Col xs={24} sm={12} xl={6} style={props.style}>
		{props.children}
	</Col>
);

interface LegendProps {
	title?: string;
	children: React.ReactNode;
	defaultOpen?: boolean;
}

interface SectionProps {
	title: string;
	subtitle?: string;
	children: React.ReactNode;
}

export const LegendFields = {
	paper: {
		padding: (
			<Typography.Text className="legend-text">
				<div className="legend-color legend-color_paper-padding" /> - Paper padding / Grid margin
			</Typography.Text>
		),
		spacing: (
			<Typography.Text className="legend-text">
				<div className="legend-color legend-color_paper-bg" /> - Grid spacing
			</Typography.Text>
		),
		info: (
			<Typography.Text italic>
				Blue border around paper is not printed and is used as visual separator for preview only
			</Typography.Text>
		),
	},
	label: {
		padding: (
			<Typography.Text className="legend-text">
				<div className="legend-color legend-color_label-paddings" />
				- Non-printable label paddings
			</Typography.Text>
		),
		images: (
			<Typography.Text className="legend-text">
				<div className="legend-color legend-color_label-icons" />
				- Printable section (crematory logo and pet qr-code)
			</Typography.Text>

		),
		texts: (
			<Typography.Text className="legend-text">
				<div className="legend-color legend-color_label-text" />
				- Printable section (text)
			</Typography.Text>
		),
		info: (
			<Typography.Text italic>
				Black border around label (cell) is not printed and is used as visual separator for preview only
			</Typography.Text>
		),
	},
};

export const Legend: React.FC<LegendProps> = (props: LegendProps) => {
	return (
		<Collapse
			expandIconPosition="end"
			style={{ background: 'inherit' }}
			defaultActiveKey={props.defaultOpen ? 'legend' : undefined}
			expandIcon={({ isActive }) => (
				isActive ? (
					<Typography.Link>
						Hide
					</Typography.Link>
				) : (
					<Typography.Link>
						Show
					</Typography.Link>
				)
			)}
		>
			<Collapse.Panel header={props.title ?? 'Legend'} key="legend">
				{props.children}
			</Collapse.Panel>
		</Collapse>
	);
};

export const Section: React.FC<SectionProps> = (props: SectionProps) => {
	return (
		<Collapse
			defaultActiveKey="section"
			expandIconPosition="end"
			expandIcon={({ isActive }) => (
				isActive ? (
					<Typography.Link>
						Hide
					</Typography.Link>
				) : (
					<Typography.Link>
						Show
					</Typography.Link>
				)
			)}
			ghost
			className="section"
		>
			<Collapse.Panel
				key="section"
				header={(
					<span className="card__heading">
						<div>
							{props.title}
						</div>
						<div className="card__heading__subtitle">
							{props.subtitle}
						</div>
					</span>
				)}
			>
				{props.children}
			</Collapse.Panel>
		</Collapse>
	);
};

export function getRandomName(): string {
	const first = dictionary.first[Math.floor(Math.random() * dictionary.first.length)];
	const last = dictionary.last[Math.floor(Math.random() * dictionary.last.length)];

	return `${first} ${last}`;
}

export function getRandomPetName(): string {
	return dictionary.pet[Math.floor(Math.random() * dictionary.pet.length)];
}

export function getRandomClinicName(): string {
	const name = dictionary.last[Math.floor(Math.random() * dictionary.last.length)];

	return `${name} Animal Clinic`;
}

export function getRandomDigit(): string {
	return (Math.random() * 10).toFixed(0);
}

export function getRandomPhone(): string {
	const digits = [
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
		getRandomDigit(),
	];

	return `+1 (9${digits[0]}${digits[1]}) ${digits[2]}${digits[3]}${digits[4]} ${digits[5]}${digits[6]}-${digits[7]}${digits[8]}`;
}

export function getLabels(count: number): Array<LabelData> {
	const clinic = getRandomClinicName();

	return Array(count)
		.fill(0)
		.map((_, id: number) => ({
			id: id.toFixed(0),

			pet: getRandomPetName(),
			owner: getRandomName(),
			clinic,
			phone: getRandomPhone(),
		}));
}
